import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

const REGISTERED_STEPS = [
  {
    timing: "At registration",
    title: "Registration confirmed",
    detail: "Confirmation with calendar and attendance links.",
  },
  {
    timing: "24 hours before",
    title: "Event reminder",
    detail: "Reminder with the event details and join link.",
  },
  {
    timing: "5 minutes before",
    title: "We go live in 5 minutes",
    detail: "Final reminder to join the live event.",
  },
]

const ELIGIBLE_AUDIENCE_STEPS = [
  {
    timing: "3 days before",
    title: "Registration invitation",
    detail: "Invite eligible members to reserve their spot.",
  },
  {
    timing: "24 hours before",
    title: "Registration invitation",
    detail: "A second invitation for members who still have not registered.",
  },
  {
    timing: "5 minutes before",
    title: "We go live in 5 minutes",
    detail: "Final opportunity to register and join live.",
  },
]

const NEW_MEMBER_STEPS = [
  {
    timing: "3 days before",
    title: "New Member Call invitation",
    detail: "Invite qualifying new paid members to reserve their spot.",
  },
  {
    timing: "24 hours before",
    title: "New Member Call invitation",
    detail: "Remind new members who still have not registered.",
  },
  {
    timing: "5 minutes before",
    title: "We go live in 5 minutes",
    detail: "Final opportunity to register and join the call.",
  },
]

const CadenceTrack = ({ title, subtitle, steps, status, muted }) => (
  <Paper
    variant="outlined"
    sx={{
      flex: "1 1 340px",
      minWidth: 0,
      p: 2,
      opacity: muted ? 0.68 : 1,
      bgcolor: "background.paper",
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={1}
      mb={1.5}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      {status}
    </Stack>
    <Divider />
    <Stack mt={1.5} spacing={1.5}>
      {steps.map((step, index) => (
        <Stack key={step.timing} direction="row" spacing={1.5} alignItems="flex-start">
          <Stack alignItems="center" sx={{ minWidth: 22 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                mt: 0.5,
                borderRadius: "50%",
                bgcolor: index === steps.length - 1 ? "success.main" : "primary.main",
              }}
            />
            {index < steps.length - 1 && (
              <Box
                sx={{
                  width: 2,
                  height: 40,
                  mt: 0.5,
                  bgcolor: "divider",
                }}
              />
            )}
          </Stack>
          <Box pb={index < steps.length - 1 ? 0.5 : 0}>
            <Chip
              size="small"
              variant="outlined"
              label={step.timing}
              sx={{ mb: 0.5, fontWeight: 600 }}
            />
            <Typography variant="body2" fontWeight={700}>
              {step.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {step.detail}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  </Paper>
)

CadenceTrack.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  status: PropTypes.node.isRequired,
  muted: PropTypes.bool,
}

const EventEmailCadence = ({
  audienceEnabled,
  newMemberEnabled,
  compact = false,
  defaultExpanded,
}) => {
  const [expanded, setExpanded] = useState(
    defaultExpanded === undefined ? false : defaultExpanded
  )
  const audienceStatus =
    audienceEnabled === true ? (
      <Chip size="small" color="success" label="Enabled" />
    ) : audienceEnabled === false ? (
      <Chip size="small" label="Disabled" />
    ) : (
      <Chip size="small" color="warning" variant="outlined" label="Event toggle required" />
    )
  const newMemberStatus =
    newMemberEnabled === true ? (
      <Chip size="small" color="success" label="Enabled" />
    ) : newMemberEnabled === false ? (
      <Chip size="small" label="Disabled" />
    ) : (
      <Chip size="small" color="warning" variant="outlined" label="Event toggle required" />
    )

  return (
    <Box
      sx={{
        mt: compact ? 1.5 : 0,
        p: compact ? 1.5 : 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        mb={expanded ? 1.5 : 0}
      >
        <Box>
          <Typography variant={compact ? "subtitle1" : "h6"} fontWeight={700}>
            Event email cadence
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current platform schedule for event confirmations, invitations, and reminders.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip size="small" color="primary" variant="outlined" label="Current cadence" />
          <IconButton
            size="small"
            onClick={() => setExpanded((value) => !value)}
            aria-label={expanded ? "Collapse event email cadence" : "Expand event email cadence"}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <CadenceTrack
            title="Registered members"
            subtitle="Sent automatically after a member registers."
            steps={REGISTERED_STEPS}
            status={<Chip size="small" color="success" label="Always active" />}
          />
          <CadenceTrack
            title="Eligible non-registrants"
            subtitle="Rechecks access and registration status at every send."
            steps={ELIGIBLE_AUDIENCE_STEPS}
            status={audienceStatus}
            muted={audienceEnabled === false}
          />
          <CadenceTrack
            title="New qualifying members"
            subtitle="Members within 21 days of first qualifying access, filtered by the event's selected tier or exact product."
            steps={NEW_MEMBER_STEPS}
            status={newMemberStatus}
            muted={newMemberEnabled === false}
          />
        </Stack>

        <Alert severity="info" sx={{ mt: 1.5 }}>
          No reminders are sent at or after the event start time. For the eligible-member
          and new-member series, passed windows are skipped when the relevant
          event toggle is enabled late. If both series qualify a member, only
          one email is sent for that window.
        </Alert>
      </Collapse>
    </Box>
  )
}

EventEmailCadence.propTypes = {
  audienceEnabled: PropTypes.bool,
  newMemberEnabled: PropTypes.bool,
  compact: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
}

export default EventEmailCadence
